import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Form, Formik } from "formik";
import moment from "moment/moment";
import { IconCircleCheck, IconSparkles } from "@tabler/icons-react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Callout from "../UI/Callout";
import Card from "../UI/Card";
import EmptyState from "../UI/EmptyState";
import Status from "../UI/Status";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
// @ts-expect-error pure js
import Spinner from "../UI/Spinner";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { useToast } from "../UI/Toast/useToast";
import useTenantHistoryAudit from "../../hooks/use-tenant-history-audit";
import type { Balance } from "../../models/balance";
import type {
  HistoryProblem,
  HistoryProblemSeverity,
  RemediationAction,
} from "../../models/tenantHistoryAudit";
import styles from "./TenantHistoryAuditDialog.module.css";

const SEVERITY_LABELS: Record<HistoryProblemSeverity, string> = {
  danger: "Anomalie",
  warning: "À vérifier",
  info: "Information",
};

const SEVERITY_ORDER: Record<HistoryProblemSeverity, number> = {
  danger: 0,
  warning: 1,
  info: 2,
};

function formatPeriod(period: string) {
  const label = moment(period, "YYYY-MM").format("MMMM YYYY");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function actionKey(problemIndex: number, actionIndex: number) {
  return `${problemIndex}:${actionIndex}`;
}

interface AuditFormValues {
  selected: string[];
}

interface TenantHistoryAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: number | undefined;
  balances: Balance[];
  getBalanceLabel: (balance: Balance) => string;
  /** Applique les corrections cochées ; rejette en cas d'erreur API. */
  onApply: (actions: RemediationAction[]) => Promise<void>;
}

export default function TenantHistoryAuditDialog({
  open,
  onOpenChange,
  tenantId,
  balances,
  getBalanceLabel,
  onApply,
}: TenantHistoryAuditDialogProps) {
  const { audit, isLoading, run, reset } = useTenantHistoryAudit(tenantId);
  const toast = useToast();
  const [error, setError] = useState<ReactNode>(null);
  const [applyError, setApplyError] = useState<ReactNode>(null);
  const ranForOpening = useRef(false);

  const balancesById = useMemo(() => {
    const map = new Map<number, Balance>();
    const visit = (balance: Balance) => {
      map.set(balance.id, balance);
      balance.balances?.forEach(visit);
    };
    balances.forEach(visit);
    return map;
  }, [balances]);

  const runAudit = useCallback(async () => {
    setError(null);
    setApplyError(null);
    try {
      await run();
    } catch (err) {
      setError(handleAPIError(err));
    }
  }, [run]);

  useEffect(() => {
    if (!open) {
      ranForOpening.current = false;
      reset();
      setError(null);
      setApplyError(null);
      return;
    }
    if (ranForOpening.current) return;
    ranForOpening.current = true;
    void runAudit();
  }, [open, reset, runAudit]);

  const problems = useMemo(
    () =>
      [...(audit?.problems ?? [])].sort(
        (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
      ),
    [audit],
  );

  // Les corrections issues des règles sont cochées d'office ; celles de l'IA
  // restent à l'appréciation de l'utilisateur.
  const initialValues = useMemo<AuditFormValues>(
    () => ({
      selected: problems.flatMap((problem, i) =>
        problem.source === "rules"
          ? problem.actions.map((_, j) => actionKey(i, j))
          : [],
      ),
    }),
    [problems],
  );

  async function submitHandler(values: AuditFormValues) {
    const actions = values.selected
      .map((key) => {
        const [i, j] = key.split(":").map(Number);
        return problems[i]?.actions[j];
      })
      .filter((action): action is RemediationAction => action !== undefined);
    if (actions.length === 0) return;
    setApplyError(null);
    try {
      await onApply(actions);
    } catch (err) {
      setApplyError(handleAPIError(err));
      return;
    }
    toast.success({
      title:
        actions.length === 1
          ? "1 correction appliquée"
          : `${actions.length} corrections appliquées`,
      description: "L'historique est réanalysé.",
    });
    await runAudit();
  }

  function renderProblem(problem: HistoryProblem, index: number) {
    const lines = problem.balance_ids
      .map((id) => balancesById.get(id))
      .filter((balance): balance is Balance => balance !== undefined);
    return (
      <Card padding="md" key={index} className={styles.problem}>
        <div className={styles.problemHead}>
          <Status
            color={problem.severity}
            weight="medium"
            text={SEVERITY_LABELS[problem.severity]}
          />
          {problem.source === "ia" && (
            <Status color="violet" icon={IconSparkles} text="IA" />
          )}
          {problem.period && (
            <Status color="neutral" text={formatPeriod(problem.period)} />
          )}
        </div>
        <Title variant="deca" weight="bold" as="h3">
          {problem.title}
        </Title>
        <Text>{problem.detail}</Text>
        {lines.length > 0 && (
          <ul className={styles.lines}>
            {lines.map((balance) => (
              <Text as="li" key={balance.id}>
                {moment(balance.date_transac).format("DD/MM/YYYY")} ·{" "}
                {getBalanceLabel(balance)} ·{" "}
                {currencyFormatter(balance.montant, { withSign: true })}
              </Text>
            ))}
          </ul>
        )}
        {problem.fix && (
          <Text>
            <Text as="span" weight="medium">
              Correction :{" "}
            </Text>
            {problem.fix}
          </Text>
        )}
        {problem.actions.length > 0 && (
          <div className={styles.actions}>
            <Text weight="medium">Corrections proposées</Text>
            {problem.actions.map((action, j) => (
              <Checkbox
                key={actionKey(index, j)}
                name="selected"
                value={actionKey(index, j)}
              >
                {action.label}
              </Checkbox>
            ))}
          </div>
        )}
      </Card>
    );
  }

  function renderContent() {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <Spinner width="40px" />
          <Text weight="medium">L'IA analyse l'historique de paiement…</Text>
          <Text color="muted">
            Cela peut prendre une vingtaine de secondes.
          </Text>
        </div>
      );
    }
    if (error) {
      return (
        <Callout variant="danger" title="Analyse impossible">
          {error}
        </Callout>
      );
    }
    if (!audit) return null;
    return (
      <>
        {!audit.ai && (
          <Callout variant="neutral">
            L'analyse par l'IA n'est pas disponible pour le moment : seules les
            vérifications automatiques sont affichées.
          </Callout>
        )}
        {audit.summary && (
          <Callout variant="violet" title="Synthèse">
            {audit.summary}
          </Callout>
        )}
        {applyError && (
          <Callout variant="danger" title="Corrections non appliquées">
            {applyError}
          </Callout>
        )}
        {problems.length === 0 ? (
          <EmptyState
            icon={IconCircleCheck}
            title="Aucune anomalie détectée"
            description="L'historique est cohérent avec les conditions du bail."
          />
        ) : (
          <div className={styles.problems}>{problems.map(renderProblem)}</div>
        )}
      </>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={submitHandler}
    >
      {({ values, resetForm }) => (
        <ModalDialog
          size="md"
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) resetForm();
            onOpenChange(nextOpen);
          }}
          header="Analyse de l'historique de paiement"
          subtitle="Paiements en double, appels supprimés, dépôt de garantie incohérent… l'analyse signale ce qui fausse le solde et propose des corrections. Seules les corrections que vous cochez sont appliquées."
        >
          <Form>
            <ModalDialogBody className={styles.body}>
              {renderContent()}
            </ModalDialogBody>
            <ModalDialogFooter
              start={
                <Button
                  variant="secondary"
                  icon={IconSparkles}
                  disabled={isLoading}
                  onClick={() => void runAudit()}
                >
                  Relancer l'analyse
                </Button>
              }
            >
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              <FormikSubmitButton
                requiresWrite
                disabled={isLoading || values.selected.length === 0}
              >
                {values.selected.length <= 1
                  ? "Appliquer la correction"
                  : `Appliquer ${values.selected.length} corrections`}
              </FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
